import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ShoppingBag,
  Users,
  LogOut,
  Lock,
  Loader2,
  TrendingUp,
  Package,
  DollarSign,
  Percent,
  Mail,
  Phone,
  Plus,
  AlertCircle,
  Search,
  Kanban,
  ChevronRight,
  ChevronLeft,
  Send,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Download,
  RefreshCw,
  FileText,
  Upload,
  Trash2,
  Award,
  GraduationCap,
  UserCheck,
  X,
  BarChart3,
  Ticket,
  FlaskConical,
  Copy,
  CalendarClock,
} from "lucide-react";
import {
  adminLogin,
  adminLogout,
  adminVerify,
  adminStats,
  adminOrders,
  adminContacts,
  adminCreateContact,
  adminTransactions,
  adminUpdateContactStage,
  getAdminToken,
  adminApi,
  api,
  downloadAdminCsv,
  adminRefundOrder,
  adminBroadcast,
  adminBroadcastPreview,
  adminBroadcastTest,
  adminListMaterials,
  adminUploadMaterial,
  adminUpdateMaterial,
  adminDeleteMaterial,
  adminMarkOrderCompleted,
  adminUnmarkOrderCompleted,
  adminOpenCertificatePreview,
  adminListStudents,
  adminContactsBulkEmail,
  adminCampaigns,
  adminSalesDaily,
  adminCreateDiscountCode,
  adminListDiscountCodes,
  adminDeleteDiscountCode,
  adminAbStats,
  adminOnboardingBookings,
} from "../lib/api";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import ContactDrawer from "./ContactDrawer";
import RichTextEditor from "./ui/RichTextEditor";

export default function AdminDashboard() {
  const [auth, setAuth] = useState({ checking: true, ok: false });
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [drawerContact, setDrawerContact] = useState(null);

  // Expose globally for tab components to open drawer
  useEffect(() => {
    window.__openContactDrawer = (contact) => setDrawerContact(contact);
    return () => { delete window.__openContactDrawer; };
  }, []);

  useEffect(() => {
    document.title = "Admin — MyDigitalAction";
    (async () => {
      const t = getAdminToken();
      if (!t) return setAuth({ checking: false, ok: false });
      try {
        await adminVerify();
        setAuth({ checking: false, ok: true });
      } catch (e) {
        // Token expired or invalid — force re-login.
        console.warn("Admin token invalid, requesting re-login:", e?.response?.status || e?.message);
        setAuth({ checking: false, ok: false });
      }
    })();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(null);
    setLoginLoading(true);
    try {
      await adminLogin(password);
      setAuth({ checking: false, ok: true });
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Password non valida");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    await adminLogout();
    setAuth({ checking: false, ok: false });
  };

  if (auth.checking) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 size={32} className="text-white/60 animate-spin" />
      </div>
    );
  }

  if (!auth.ok) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 justify-center">
              <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center flex-shrink-0">
                <span className="font-bebas text-black text-xl leading-none">MDA</span>
              </div>
              <div className="flex flex-col leading-none text-left">
                <span className="font-display font-semibold text-white text-base tracking-tight">
                  MyDigitalAction<span style={{ color: "#d4ff3d" }}>.</span>
                </span>
                <span className="font-mono-label text-[10px] mt-1" style={{ color: "#d4ff3d", letterSpacing: "0.3em" }}>
                  EXPERIENCE
                </span>
              </div>
            </div>
            <h1 className="font-display text-2xl text-white mt-6">Admin</h1>
            <p className="text-white/50 text-sm mt-2">Accedi per gestire vendite e contatti</p>
          </div>

          <form onSubmit={handleLogin} className="bg-card border border-white/10 rounded-2xl p-6">
            <label className="block font-mono-label text-white/50 text-[11px] mb-2 flex items-center gap-2">
              <Lock size={14} /> Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#141414] border border-white/10 rounded-xl pl-4 pr-11 py-3 text-white text-sm focus:outline-none focus:border-white/30 transition-colors"
                placeholder="••••••••"
                autoComplete="current-password"
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors p-1"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {loginError && (
              <div className="mt-3 p-3 rounded-lg border border-red-400/30 bg-red-500/10 text-red-300 text-xs flex items-center gap-2" data-testid="admin-login-error">
                <AlertCircle size={13} /> {loginError}
              </div>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loginLoading || !password}
              className={`btn-primary mt-5 w-full justify-center ${loginLoading || !password ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {loginLoading ? <Loader2 size={16} className="animate-spin" /> : "Accedi"}
            </button>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setRecoveryOpen(true)}
                className="text-white/50 hover:text-white text-xs inline-flex items-center gap-1.5"
              >
                <KeyRound size={12} /> Password dimenticata?
              </button>
            </div>
          </form>

          {recoveryOpen && <RecoveryModal onClose={() => setRecoveryOpen(false)} />}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Sidebar */}
        <aside className="lg:w-64 lg:min-h-screen bg-[#0c0c0c] border-b lg:border-b-0 lg:border-r border-white/5 flex flex-col">
          <div className="p-6 border-b border-white/5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
              <span className="font-bebas text-black text-base leading-none">MDA</span>
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display font-semibold text-white text-sm tracking-tight">
                MyDigitalAction<span style={{ color: "#d4ff3d" }}>.</span>
              </span>
              <span className="font-mono-label text-[8px] mt-0.5" style={{ color: "#d4ff3d", letterSpacing: "0.28em" }}>
                EXPERIENCE
              </span>
            </div>
            <div className="ml-auto">
              <div className="text-white/40 text-[10px] uppercase tracking-widest">Admin</div>
            </div>
          </div>

          <nav className="flex-1 p-3 lg:p-4 flex lg:flex-col gap-1 overflow-x-auto">
            <NavItem icon={<LayoutDashboard size={16} />} label="Overview" active={tab === "overview"} onClick={() => setTab("overview")} />
            <NavItem icon={<ShoppingBag size={16} />} label="Ordini" active={tab === "orders"} onClick={() => setTab("orders")} />
            <NavItem icon={<Users size={16} />} label="Contatti" active={tab === "contacts"} onClick={() => setTab("contacts")} />
            <NavItem icon={<Kanban size={16} />} label="Pipeline" active={tab === "pipeline"} onClick={() => setTab("pipeline")} />
            <NavItem icon={<Package size={16} />} label="Transazioni" active={tab === "transactions"} onClick={() => setTab("transactions")} />
            <NavItem icon={<Send size={16} />} label="Broadcast" active={tab === "broadcast"} onClick={() => setTab("broadcast")} />
            <NavItem icon={<BarChart3 size={16} />} label="Campagne" active={tab === "campaigns"} onClick={() => setTab("campaigns")} />
            <NavItem icon={<Ticket size={16} />} label="Sconti" active={tab === "discounts"} onClick={() => setTab("discounts")} />
            <NavItem icon={<FlaskConical size={16} />} label="A/B Test" active={tab === "abtest"} onClick={() => setTab("abtest")} />
            <NavItem icon={<CalendarClock size={16} />} label="Prenotazioni" active={tab === "bookings"} onClick={() => setTab("bookings")} />
            <NavItem icon={<FileText size={16} />} label="Materiali" active={tab === "materials"} onClick={() => setTab("materials")} />
            <NavItem icon={<GraduationCap size={16} />} label="Studenti" active={tab === "students"} onClick={() => setTab("students")} />
          </nav>

          <div className="p-4 border-t border-white/5">
            <button onClick={handleLogout} data-testid="admin-logout-btn" className="w-full text-white/60 hover:text-white text-sm flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors">
              <LogOut size={14} /> Esci
            </button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-6 lg:p-10 overflow-x-hidden">
          {tab === "overview" && <OverviewTab />}
          {tab === "orders" && <OrdersTab />}
          {tab === "contacts" && <ContactsTab />}
          {tab === "pipeline" && <PipelineTab />}
          {tab === "transactions" && <TransactionsTab />}
          {tab === "broadcast" && <BroadcastTab />}
          {tab === "campaigns" && <CampaignsTab />}
          {tab === "discounts" && <DiscountsTab />}
          {tab === "abtest" && <ABTestTab />}
          {tab === "bookings" && <BookingsTab />}
          {tab === "materials" && <MaterialsTab />}
          {tab === "students" && <StudentsTab />}
        </main>
      </div>
      {drawerContact && (
        <ContactDrawer
          contact={drawerContact}
          onClose={() => setDrawerContact(null)}
        />
      )}
    </div>
  );
}

function RecoveryModal({ onClose }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleRecover = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post("/admin/password/recover");
      setResult({ ok: true, message: res.data.message || "Email di recupero inviata." });
    } catch (e) {
      setResult({ ok: false, message: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={() => !loading && onClose()}
    >
      <div
        className="relative w-full max-w-md bg-[#0f0f0f] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound size={16} className="text-white/70" />
            <div className="font-display text-white text-lg">Recupera password</div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white"
            aria-label="Chiudi"
          >
            ×
          </button>
        </div>
        <div className="px-6 py-6">
          {result?.ok ? (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4" style={{ background: "#d4ff3d" }}>
                <CheckCircle2 size={24} className="text-black" />
              </div>
              <div className="font-display text-white text-lg">Email inviata</div>
              <p className="text-white/60 text-sm mt-2">{result.message}</p>
              <button onClick={onClose} className="btn-primary mt-6 w-full justify-center">Chiudi</button>
            </div>
          ) : (
            <>
              <p className="text-white/70 text-sm leading-relaxed">
                Ti invieremo un'email all'indirizzo di recupero configurato per il proprietario dell'account,
                con le istruzioni per accedere di nuovo.
              </p>
              {result && !result.ok && (
                <div className="mt-4 p-3 rounded-lg border border-red-400/30 bg-red-500/10 text-red-300 text-xs flex items-start gap-2">
                  <AlertCircle size={13} className="mt-0.5 flex-shrink-0" /> <span>{result.message}</span>
                </div>
              )}
              <button
                onClick={handleRecover}
                disabled={loading}
                className="btn-primary mt-6 w-full justify-center"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <>Invia email di recupero <Send size={14} /></>}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="w-full text-white/50 hover:text-white text-xs mt-3"
              >
                Annulla
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function NavItem({ icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`admin-nav-${String(label).toLowerCase()}`}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors flex-shrink-0 ${
        active ? "bg-white text-black" : "text-white/70 hover:text-white hover:bg-white/5"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function StatCard({ icon, label, value, suffix }) {
  return (
    <div className="bg-card border border-white/10 rounded-2xl p-5">
      <div className="flex items-center justify-between">
        <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
          {icon}
        </div>
      </div>
      <div className="font-mono-label text-white/40 text-[10px] mt-4">{label}</div>
      <div className="font-display text-white text-3xl mt-1">
        {value}
        {suffix && <span className="text-white/40 text-base ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

function SalesChart() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try { setData(await adminSalesDaily(30)); } catch (e) { console.error("sales-daily failed:", e?.message); }
    })();
  }, []);
  if (!data) return null;
  const chartData = data.daily.map((d) => ({
    ...d,
    label: `${d.date.slice(8, 10)}/${d.date.slice(5, 7)}`,
  }));
  return (
    <div className="mt-8 bg-card border border-white/10 rounded-2xl p-6" data-testid="sales-chart">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TrendingUp size={16} style={{ color: "#d4ff3d" }} />
          <div className="font-display text-white text-lg">Vendite ultimi 30 giorni</div>
        </div>
        <div className="text-white/50 text-xs">
          {data.total_orders} ordini · <span className="text-white font-medium">€{data.total_revenue.toFixed(2)}</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} tickLine={false} axisLine={false} interval={4} />
          <YAxis yAxisId="rev" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} tickLine={false} axisLine={false} />
          <YAxis yAxisId="ord" orientation="right" hide />
          <Tooltip
            contentStyle={{ background: "#141414", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, fontSize: 12 }}
            labelStyle={{ color: "#fff" }}
            formatter={(value, name) => name === "Ricavi" ? [`€${Number(value).toFixed(2)}`, "Ricavi"] : [value, "Ordini"]}
          />
          <Bar yAxisId="rev" dataKey="revenue" name="Ricavi" fill="#d4ff3d" radius={[4, 4, 0, 0]} maxBarSize={22} />
          <Line yAxisId="ord" type="monotone" dataKey="orders" name="Ordini" stroke="#ffffff" strokeWidth={1.5} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function OverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await adminStats();
        setData(s);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="flex items-center gap-2 text-white/60"><Loader2 size={16} className="animate-spin" /> Caricamento...</div>;
  if (error) return <div className="text-red-400 text-sm">{error}</div>;

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-display text-3xl lg:text-4xl text-white">Overview</h1>
        <p className="text-white/50 text-sm mt-2">Statistiche in tempo reale del tuo business</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={<DollarSign size={16} className="text-lime" style={{ color: "#d4ff3d" }} />} label="Ricavo totale" value={`€${data.total_revenue.toFixed(2)}`} />
        <StatCard icon={<ShoppingBag size={16} className="text-white/70" />} label="Ordini pagati" value={data.orders_count} />
        <StatCard icon={<Users size={16} className="text-white/70" />} label="Contatti" value={data.contacts_count} />
        <StatCard icon={<Percent size={16} className="text-white/70" />} label="Conversion rate" value={data.conversion_rate.toFixed(1)} suffix="%" />
        <StatCard icon={<UserCheck size={16} style={{ color: "#d4ff3d" }} />} label="Convertiti (30gg)" value={data.converted_30d ?? 0} />
      </div>

      {/* Grafico vendite giornaliere */}
      <SalesChart />

      {/* Email test panel */}
      <EmailTestPanel />

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={16} className="text-white/60" />
            <div className="font-display text-white text-lg">Top prodotti</div>
          </div>
          {data.top_products.length === 0 ? (
            <div className="text-white/40 text-sm py-6 text-center">Nessuna vendita ancora.</div>
          ) : (
            <ul className="space-y-3">
              {data.top_products.map((p) => (
                <li key={p.product_id} className="flex items-center justify-between border-b border-white/5 pb-3 last:border-0">
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-sm truncate">{p.name}</div>
                    <div className="text-white/40 text-xs mt-0.5">{p.count} ordini</div>
                  </div>
                  <div className="font-display text-white ml-3">€{p.revenue.toFixed(2)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-card border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingBag size={16} className="text-white/60" />
            <div className="font-display text-white text-lg">Ultimi ordini</div>
          </div>
          {data.last_orders.length === 0 ? (
            <div className="text-white/40 text-sm py-6 text-center">Nessun ordine ancora.</div>
          ) : (
            <ul className="space-y-3">
              {data.last_orders.map((o) => (
                <li key={o.id} className="flex items-center justify-between border-b border-white/5 pb-3 last:border-0">
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-sm truncate">{o.customer_name || o.customer_email}</div>
                    <div className="text-white/40 text-xs mt-0.5 truncate">{o.product_name}</div>
                  </div>
                  <div className="font-display text-white ml-3 text-sm">€{o.amount}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function EmailTestPanel() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSend = async () => {
    if (!email || !email.includes("@")) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await adminApi.post("/admin/email/test", { to: email });
      setResult({ ok: res.data.sent, message: res.data.sent ? `Email inviata a ${email}` : (res.data.reason || "Invio fallito") });
    } catch (e) {
      setResult({ ok: false, message: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-6 bg-card border border-white/10 rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <Send size={16} className="text-white/60" />
        <div className="font-display text-white text-lg">Test invio email</div>
      </div>
      <p className="text-white/50 text-xs mb-4">
        Invia un'email di conferma di prova al tuo indirizzo per verificare template e deliverability.
      </p>
      <div className="flex gap-3 flex-wrap">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="tua@email.it"
          className="flex-1 min-w-[240px] bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30"
        />
        <button
          onClick={handleSend}
          disabled={loading || !email.includes("@")}
          className={`btn-primary ${loading || !email.includes("@") ? "opacity-50 cursor-not-allowed" : ""}`}
          style={{ padding: "10px 18px", fontSize: 13 }}
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} /> Invia test</>}
        </button>
      </div>
      {result && (
        <div className={`mt-3 p-3 rounded-lg text-xs flex items-center gap-2 ${
          result.ok
            ? "border border-lime-400/30 bg-lime-500/10 text-lime-300"
            : "border border-red-400/30 bg-red-500/10 text-red-300"
        }`}>
          {result.ok ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
          {result.message}
        </div>
      )}
      <div className="mt-3 text-white/40 text-[11px]">
        Mittente: <span className="text-white/60">noreply@mydigitalaction.it</span> (transazionali) · <span className="text-white/60">frank@mydigitalaction.it</span> (relazionali)
      </div>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [refundingId, setRefundingId] = useState(null);
  const [completingId, setCompletingId] = useState(null);

  const load = async () => {
    try {
      const data = await adminOrders();
      setOrders(data);
    } catch (e) {
      console.error("Failed to load orders:", e?.response?.data || e?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleExport = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      await downloadAdminCsv("/admin/orders/export.csv", `ordini-mda-${today}.csv`);
    } catch (e) {
      alert("Errore export CSV: " + (e?.response?.data?.detail || e?.message));
    }
  };

  const handleRefund = async (order) => {
    const ok = window.confirm(
      `Confermi il rimborso di €${order.amount} a ${order.customer_email}?\n\nQuesta azione è IRREVERSIBILE su Stripe.`
    );
    if (!ok) return;
    setRefundingId(order.session_id);
    try {
      const res = await adminRefundOrder(order.session_id);
      alert(`✅ Rimborso effettuato: €${res.amount}\nID Stripe: ${res.refund_id}`);
      await load();
    } catch (e) {
      alert("❌ Errore rimborso: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setRefundingId(null);
    }
  };

  const handleToggleCompleted = async (order) => {
    const isCompleted = !!order.completed_at;
    const ok = window.confirm(
      isCompleted
        ? `Vuoi RIMUOVERE il completamento per ${order.customer_email}?\nIl certificato non sarà più scaricabile.`
        : `Confermi il completamento del corso per ${order.customer_email}?\nLo studente potrà scaricare il certificato dall'Area Studente.`
    );
    if (!ok) return;
    setCompletingId(order.session_id);
    try {
      if (isCompleted) {
        await adminUnmarkOrderCompleted(order.session_id);
      } else {
        await adminMarkOrderCompleted(order.session_id);
      }
      await load();
    } catch (e) {
      alert("❌ Errore: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setCompletingId(null);
    }
  };

  const filtered = orders.filter((o) =>
    !q || o.customer_email?.toLowerCase().includes(q.toLowerCase()) ||
    o.customer_name?.toLowerCase().includes(q.toLowerCase()) ||
    o.product_name?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl text-white">Ordini</h1>
          <p className="text-white/50 text-sm mt-2">{orders.length} ordini pagati totali</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex items-center gap-2 bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-white text-sm px-4 py-2 rounded-lg transition-colors"
            data-testid="admin-orders-export-btn"
          >
            <Download size={14} /> Esporta CSV
          </button>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca email, nome, prodotto..." className="bg-card border border-white/10 rounded-lg pl-9 pr-4 py-2 text-white text-sm w-64 focus:outline-none focus:border-white/30" />
          </div>
        </div>
      </div>

      {loading ? <div className="text-white/60 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Caricamento...</div> : (
        <div className="bg-card border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02] border-b border-white/5">
                <tr className="text-left font-mono-label text-[10px] text-white/50 uppercase tracking-widest">
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3">Prodotto</th>
                  <th className="px-4 py-3">Importo</th>
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="text-white">{o.customer_name || "—"}</div>
                      <div className="text-white/40 text-xs">{o.customer_email}</div>
                    </td>
                    <td className="px-4 py-3 text-white/80">{o.product_name}</td>
                    <td className="px-4 py-3 font-display text-white">
                      €{o.amount}
                      {o.refunded && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20">Rimborsato</span>}
                      {o.completed_at && !o.refunded && (
                        <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full border" style={{ background: "rgba(212,255,61,0.1)", borderColor: "rgba(212,255,61,0.35)", color: "#d4ff3d" }}>
                          Completato
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white/50 text-xs">{new Date(o.created_at).toLocaleString("it-IT")}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-4 justify-end">
                        {!o.refunded && (
                          <>
                            <button
                              type="button"
                              onClick={() => adminOpenCertificatePreview(o.session_id)}
                              className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white transition-colors"
                              title="Anteprima PDF certificato con i dati attuali dell'ordine"
                              data-testid={`admin-cert-preview-${o.id}`}
                            >
                              <Eye size={12} /> Anteprima
                            </button>
                            <button
                              type="button"
                              onClick={() => handleToggleCompleted(o)}
                              disabled={completingId === o.session_id}
                              className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white disabled:opacity-40 transition-colors"
                              data-testid={`admin-complete-${o.id}`}
                              title={o.completed_at ? "Rimuovi completamento" : "Segna come completato → sblocca certificato"}
                            >
                              {completingId === o.session_id ? <Loader2 size={12} className="animate-spin" /> : <Award size={12} />}
                              {o.completed_at ? "Annulla" : "Completa"}
                            </button>
                          </>
                        )}
                        {o.refunded ? (
                          <span className="text-white/30 text-xs">—</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleRefund(o)}
                            disabled={refundingId === o.session_id}
                            className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-red-400 disabled:opacity-40 transition-colors"
                            data-testid={`admin-refund-${o.id}`}
                          >
                            {refundingId === o.session_id ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                            {refundingId === o.session_id ? "..." : "Rimborsa"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-white/40">Nessun ordine trovato.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ContactsTab() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newContact, setNewContact] = useState({ email: "", name: "", phone: "", notes: "" });
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminContacts();
      setContacts(data);
    } catch (e) {
      console.error("Failed to load contacts:", e?.response?.data || e?.message);
    }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleCreate = async () => {
    if (!newContact.email) return;
    setCreating(true);
    try {
      await adminCreateContact({ ...newContact, source: "manual" });
      setNewContact({ email: "", name: "", phone: "", notes: "" });
      setShowNew(false);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore");
    } finally { setCreating(false); }
  };

  const filtered = contacts.filter((c) =>
    !q || c.email?.toLowerCase().includes(q.toLowerCase()) ||
    c.name?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl text-white">Contatti / CRM</h1>
          <p className="text-white/50 text-sm mt-2">{contacts.length} contatti totali</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {selected.size > 0 && (
            <button
              onClick={() => setBulkOpen(true)}
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg font-semibold"
              style={{ background: "#d4ff3d", color: "#0a0a0a" }}
              data-testid="contacts-bulk-email-btn"
            >
              <Send size={14} /> Invia email ({selected.size})
            </button>
          )}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca..." className="bg-card border border-white/10 rounded-lg pl-9 pr-4 py-2 text-white text-sm w-56 focus:outline-none focus:border-white/30" />
          </div>
          <button
            type="button"
            onClick={async () => {
              try {
                const today = new Date().toISOString().slice(0, 10);
                await downloadAdminCsv("/admin/contacts/export.csv", `contatti-mda-${today}.csv`);
              } catch (e) {
                alert("Errore export: " + (e?.response?.data?.detail || e?.message));
              }
            }}
            className="inline-flex items-center gap-2 bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-white text-sm px-4 py-2 rounded-lg transition-colors"
            data-testid="admin-contacts-export-btn"
          >
            <Download size={14} /> Esporta CSV
          </button>
          <button onClick={() => setShowNew(!showNew)} className="btn-primary" style={{ padding: "10px 16px", fontSize: 13 }}>
            <Plus size={14} /> Nuovo
          </button>
        </div>
      </div>

      {showNew && (
        <div className="bg-card border border-white/10 rounded-2xl p-5 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Email *" type="email" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} className="bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
            <input placeholder="Nome" value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} className="bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
            <input placeholder="Telefono" value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} className="bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
            <input placeholder="Note" value={newContact.notes} onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })} className="bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
          </div>
          <div className="flex gap-2 mt-3 justify-end">
            <button onClick={() => setShowNew(false)} className="text-white/60 hover:text-white text-sm px-4">Annulla</button>
            <button onClick={handleCreate} disabled={!newContact.email || creating} className="btn-primary" style={{ padding: "8px 14px", fontSize: 12 }}>
              {creating ? <Loader2 size={12} className="animate-spin" /> : "Crea contatto"}
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="text-white/60 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Caricamento...</div> : (
        <div className="bg-card border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02] border-b border-white/5">
                <tr className="text-left font-mono-label text-[10px] text-white/50 uppercase tracking-widest">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      className="accent-lime cursor-pointer"
                      style={{ accentColor: "#d4ff3d" }}
                      checked={filtered.length > 0 && filtered.every((c) => selected.has(c.id))}
                      onChange={(e) => {
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) filtered.forEach((c) => next.add(c.id));
                          else filtered.forEach((c) => next.delete(c.id));
                          return next;
                        });
                      }}
                      data-testid="contacts-select-all"
                    />
                  </th>
                  <th className="px-4 py-3">Contatto</th>
                  <th className="px-4 py-3">Telefono</th>
                  <th className="px-4 py-3">Fonte</th>
                  <th className="px-4 py-3">Ordini</th>
                  <th className="px-4 py-3">Speso</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                    onClick={() => window.__openContactDrawer && window.__openContactDrawer(c)}
                  >
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="cursor-pointer"
                        style={{ accentColor: "#d4ff3d" }}
                        checked={selected.has(c.id)}
                        onChange={() => toggleSelect(c.id)}
                        data-testid={`contact-select-${c.id}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white">{c.name || "—"}</div>
                      <div className="text-white/40 text-xs flex items-center gap-1"><Mail size={10} /> {c.email}</div>
                    </td>
                    <td className="px-4 py-3 text-white/60 text-xs">
                      {c.phone ? <span className="inline-flex items-center gap-1"><Phone size={10} /> {c.phone}</span> : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const src = c.source || "";
                        const isQuiz = src === "quiz";
                        return (
                          <span
                            data-testid={`contact-source-${src}`}
                            className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border ${
                              isQuiz
                                ? "text-black font-semibold"
                                : "text-white/70 border-white/10"
                            }`}
                            style={
                              isQuiz
                                ? { background: "#d4ff3d", borderColor: "#d4ff3d" }
                                : {}
                            }
                          >
                            {isQuiz && <span style={{ fontSize: "11px" }}>✨</span>}
                            {src || "—"}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-white/80">{c.orders_count}</td>
                    <td className="px-4 py-3 font-display text-white">€{c.total_spent.toFixed(2)}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">Nessun contatto trovato.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {bulkOpen && (
        <BulkEmailModal
          contactIds={Array.from(selected)}
          contacts={contacts.filter((c) => selected.has(c.id))}
          onClose={() => setBulkOpen(false)}
          onSent={() => { setBulkOpen(false); setSelected(new Set()); }}
        />
      )}
    </div>
  );
}

function BulkEmailModal({ contactIds, contacts, onClose, onSent }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("Ciao,\n\n");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const withEmail = contacts.filter((c) => c.email && !c.unsubscribed);

  const send = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true); setError(null);
    try {
      const html_body = body.trim().split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`).join("\n");
      const res = await adminContactsBulkEmail({ contact_ids: contactIds, subject: subject.trim(), html_body });
      setResult(res);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center px-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-[#0f0f0f] border border-white/15 rounded-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="bulk-email-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-mono-label text-white/40 text-[10px]">Email di massa</div>
            <div className="font-display text-white text-lg mt-1">A: {withEmail.length} contatti</div>
            <div className="text-white/40 text-[11px] mt-1">Da: Frank Gheri &lt;frank@mydigitalaction.it&gt;</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white">
            <X size={14} />
          </button>
        </div>

        {result ? (
          <div className="py-4">
            <div className="p-4 rounded-lg border" style={{ background: "rgba(212,255,61,0.06)", borderColor: "rgba(212,255,61,0.3)" }}>
              <div className="font-display text-white text-lg">✅ Invio completato</div>
              <div className="text-white/70 text-xs mt-2">
                Inviate: <strong>{result.sent}</strong><br />
                Fallite: {result.failed}<br />
                Saltate (no email / unsubscribed): {result.skipped}
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button onClick={onSent} className="text-xs px-4 py-2 rounded-lg font-semibold" style={{ background: "#d4ff3d", color: "#0a0a0a" }} data-testid="bulk-email-close">Chiudi</button>
            </div>
          </div>
        ) : (
          <>
            <label className="block text-white/70 text-[11px] uppercase tracking-widest font-mono mb-2">Oggetto</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)}
              className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30 mb-4"
              placeholder="Es. Novità sui corsi di settembre"
              data-testid="bulk-email-subject" />
            <label className="block text-white/70 text-[11px] uppercase tracking-widest font-mono mb-2">Messaggio</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8}
              className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30 resize-y"
              data-testid="bulk-email-body" />
            <div className="text-white/40 text-[10px] mt-1">Stesso template della email singola. Firma di Frank aggiunta in fondo. I contatti unsubscribed vengono saltati.</div>
            {error && <div className="mt-3 text-red-400 text-sm">{error}</div>}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={onClose} className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/70 hover:text-white">Annulla</button>
              <button onClick={send} disabled={sending || !subject.trim() || !body.trim()}
                className="text-xs px-4 py-2 rounded-lg font-semibold disabled:opacity-50 inline-flex items-center gap-2"
                style={{ background: "#d4ff3d", color: "#0a0a0a" }}
                data-testid="bulk-email-send">
                {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                {sending ? "Invio..." : `Invia a ${withEmail.length}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PipelineTab() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [movingId, setMovingId] = useState(null);

  const STAGES = [
    { id: "lead", label: "Lead", color: "#8b8b8b" },
    { id: "contacted", label: "Contattato", color: "#6b8bff" },
    { id: "qualified", label: "Qualificato", color: "#3b5bff" },
    { id: "proposal", label: "Proposta", color: "#ffcc00" },
    { id: "won", label: "Cliente", color: "#d4ff3d" },
    { id: "lost", label: "Perso", color: "#ff6b6b" },
  ];

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminContacts();
      setContacts(data);
    } catch (e) {
      console.error("Failed to load contacts:", e?.response?.data || e?.message);
    }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const moveContact = async (contactId, newStage) => {
    setMovingId(contactId);
    // Optimistic update
    setContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, stage: newStage } : c)));
    try {
      await adminUpdateContactStage(contactId, newStage);
    } catch (e) {
      alert("Errore aggiornamento stage");
      load();
    } finally {
      setMovingId(null);
    }
  };

  const byStage = (stageId) => contacts.filter((c) => (c.stage || "lead") === stageId);

  return (
    <div>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl text-white">Pipeline CRM</h1>
          <p className="text-white/50 text-sm mt-2">
            Trascina o clicca le frecce per spostare i contatti attraverso il funnel di vendita
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          {STAGES.map((s) => (
            <div key={s.id} className="flex items-center gap-2 text-white/60">
              <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
              {s.label}
              <span className="text-white/40">({byStage(s.id).length})</span>
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-white/60 flex items-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Caricamento...
        </div>
      ) : (
        <div className="overflow-x-auto pb-4">
          <div className="grid grid-flow-col auto-cols-[280px] gap-4">
            {STAGES.map((stage, stageIdx) => {
              const stageContacts = byStage(stage.id);
              return (
                <div
                  key={stage.id}
                  className="bg-card border border-white/10 rounded-2xl p-4 min-h-[500px] flex flex-col"
                >
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: stage.color }} />
                      <span className="font-display text-white text-sm">{stage.label}</span>
                    </div>
                    <span className="font-mono-label text-white/40 text-[10px]">
                      {stageContacts.length}
                    </span>
                  </div>

                  <div className="flex-1 space-y-2 overflow-y-auto">
                    {stageContacts.length === 0 && (
                      <div className="text-white/25 text-xs text-center py-8">
                        Nessun contatto
                      </div>
                    )}
                    {stageContacts.map((c) => (
                      <PipelineCard
                        key={c.id}
                        contact={c}
                        stages={STAGES}
                        currentIdx={stageIdx}
                        onMove={moveContact}
                        moving={movingId === c.id}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 text-white/40 text-xs">
        💡 I contatti che completano un acquisto vengono automaticamente spostati in <span className="text-white/60">Cliente</span>.
      </div>
    </div>
  );
}

function PipelineCard({ contact, stages, currentIdx, onMove, moving }) {
  const canMoveLeft = currentIdx > 0;
  const canMoveRight = currentIdx < stages.length - 1;

  const handleClick = () => {
    if (window.__openContactDrawer) window.__openContactDrawer(contact);
  };

  return (
    <div
      onClick={handleClick}
      className="bg-[#141414] border border-white/10 rounded-xl p-3 group hover:border-white/25 transition-colors cursor-pointer"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-white text-sm font-medium truncate">
            {contact.name || contact.email}
          </div>
          <div className="text-white/40 text-[10px] truncate mt-0.5 flex items-center gap-1">
            <Mail size={9} /> {contact.email}
          </div>
          {contact.phone && (
            <div className="text-white/40 text-[10px] truncate mt-0.5 flex items-center gap-1">
              <Phone size={9} /> {contact.phone}
            </div>
          )}
        </div>
        {moving && <Loader2 size={12} className="text-white/50 animate-spin flex-shrink-0" />}
      </div>

      {contact.total_spent > 0 && (
        <div className="mt-2 font-display text-lime text-sm" style={{ color: "#d4ff3d" }}>
          €{contact.total_spent.toFixed(0)}
          <span className="text-white/40 text-[10px] ml-1">
            · {contact.orders_count} ordini
          </span>
        </div>
      )}

      {contact.tags && contact.tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {contact.tags.slice(0, 3).map((t, i) => (
            <span key={i} className="text-[9px] uppercase tracking-widest text-white/50 border border-white/10 rounded px-1.5 py-0.5">
              {t}
            </span>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
        <button
          disabled={!canMoveLeft || moving}
          onClick={(e) => { e.stopPropagation(); if (canMoveLeft) onMove(contact.id, stages[currentIdx - 1].id); }}
          className="flex-1 flex items-center justify-center py-1 rounded border border-white/10 text-white/60 hover:text-white hover:border-white/30 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={12} />
        </button>
        <button
          disabled={!canMoveRight || moving}
          onClick={(e) => { e.stopPropagation(); if (canMoveRight) onMove(contact.id, stages[currentIdx + 1].id); }}
          className="flex-1 flex items-center justify-center py-1 rounded border border-white/10 text-white/60 hover:text-white hover:border-white/30 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
}

function TransactionsTab() {
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await adminTransactions();
        setTxs(data);
      } catch (e) {
        console.error("Failed to load transactions:", e?.response?.data || e?.message);
      }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-display text-3xl lg:text-4xl text-white">Transazioni</h1>
        <p className="text-white/50 text-sm mt-2">Tutte le sessioni checkout ({txs.length})</p>
      </div>

      {loading ? <div className="text-white/60 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Caricamento...</div> : (
        <div className="bg-card border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02] border-b border-white/5">
                <tr className="text-left font-mono-label text-[10px] text-white/50 uppercase tracking-widest">
                  <th className="px-4 py-3">Session</th>
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3">Prodotto</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Importo</th>
                </tr>
              </thead>
              <tbody>
                {txs.map((t) => (
                  <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-white/50 text-xs font-mono">{t.session_id.slice(0, 16)}...</td>
                    <td className="px-4 py-3">
                      <div className="text-white text-xs">{t.customer_name || "—"}</div>
                      <div className="text-white/40 text-[10px]">{t.customer_email || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-white/80 text-xs">{t.product_name}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={t.status} />
                    </td>
                    <td className="px-4 py-3 font-display text-white">€{t.amount}</td>
                  </tr>
                ))}
                {txs.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-white/40">Nessuna transazione.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid: { bg: "rgba(212,255,61,0.15)", color: "#d4ff3d", label: "Pagato" },
    initiated: { bg: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.7)", label: "Iniziato" },
    pending: { bg: "rgba(255,200,0,0.15)", color: "#ffcc00", label: "In attesa" },
    expired: { bg: "rgba(255,100,100,0.15)", color: "#ff6b6b", label: "Scaduto" },
    failed: { bg: "rgba(255,100,100,0.15)", color: "#ff6b6b", label: "Fallito" },
  };
  const s = map[status] || map.initiated;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function BroadcastTab() {
  const [segment, setSegment] = useState("leads");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testEmail, setTestEmail] = useState("frank@mydigitalaction.it");
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [sampleName, setSampleName] = useState("Frank");
  const [schedule, setSchedule] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [stats, setStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);

  const bodyText = (body || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const all = await adminCampaigns();
      setStats((all || []).filter((c) => c.type === "broadcast").slice(0, 8));
    } catch (e) {
      /* silent */
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => { loadStats(); }, []);

  const openPreview = async () => {
    if (!subject.trim() || !bodyText) {
      alert("Compila subject e corpo email prima dell'anteprima");
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await adminBroadcastPreview({ subject, html_body: body, sample_name: sampleName || "Frank" });
      setPreviewHtml(res.html);
    } catch (e) {
      alert("Errore anteprima: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendTest = async () => {
    setTestResult(null);
    if (!subject.trim() || !bodyText) {
      alert("Compila subject e corpo email");
      return;
    }
    if (!testEmail.trim() || !testEmail.includes("@")) {
      alert("Inserisci un indirizzo email valido per la prova");
      return;
    }
    setTestSending(true);
    try {
      const res = await adminBroadcastTest({ subject, html_body: body, to_email: testEmail.trim(), sample_name: sampleName || "Frank" });
      setTestResult(res);
    } catch (e) {
      alert("Errore invio prova: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setTestSending(false);
    }
  };

  const SEGMENTS = [
    { id: "leads", label: "Solo Lead (non hanno ancora comprato)" },
    { id: "buyers", label: "Solo Clienti (hanno già comprato)" },
    { id: "quiz", label: "Chi ha fatto il Quiz" },
    { id: "all", label: "Tutti i contatti attivi (non unsubscribed)" },
  ];

  const handlePreview = async () => {
    setResult(null);
    try {
      const res = await adminBroadcast({ segment, subject, html_body: body, dry_run: true });
      setPreview(res);
    } catch (e) {
      alert("Errore preview: " + (e?.response?.data?.detail || e?.message));
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !bodyText) {
      alert("Compila subject e corpo email");
      return;
    }
    let scheduledIso = null;
    if (schedule) {
      if (!scheduleAt) {
        alert("Scegli data e ora di invio, oppure disattiva la programmazione");
        return;
      }
      const d = new Date(scheduleAt);
      if (isNaN(d.getTime()) || d.getTime() <= Date.now()) {
        alert("La data/ora di invio deve essere nel futuro");
        return;
      }
      scheduledIso = d.toISOString();
    }
    const when = scheduledIso
      ? `PROGRAMMATO per ${new Date(scheduledIso).toLocaleString("it-IT")}`
      : "INVIO IMMEDIATO";
    const ok = window.confirm(
      `Confermi ${when} a ${preview?.would_send_to || "?"} contatti?\n\nSubject: "${subject}"\n\nQuesta azione NON è reversibile.`
    );
    if (!ok) return;
    setSending(true);
    setResult(null);
    try {
      const res = await adminBroadcast({ segment, subject, html_body: body, dry_run: false, scheduled_at: scheduledIso });
      setResult(res);
      loadStats();
    } catch (e) {
      alert("Errore invio: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-display text-3xl lg:text-4xl text-white">Broadcast Email</h1>
        <p className="text-white/50 text-sm mt-2">Invia un'email a un segmento di contatti. Rispetta unsubscribe automaticamente.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-white/10 rounded-2xl p-6 space-y-4">
          <div>
            <label className="font-mono-label text-white/50 text-[10px] block mb-2 uppercase tracking-widest">Segmento</label>
            <select
              value={segment}
              onChange={(e) => { setSegment(e.target.value); setPreview(null); }}
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30"
              data-testid="admin-broadcast-segment"
            >
              {SEGMENTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono-label text-white/50 text-[10px] block mb-2 uppercase tracking-widest">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Es. Aggiornamento importante sui corsi di aprile"
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30"
              data-testid="admin-broadcast-subject"
            />
          </div>
          <div>
            <label className="font-mono-label text-white/50 text-[10px] block mb-2 uppercase tracking-widest">
              Corpo email
            </label>
            <RichTextEditor
              value={body}
              onChange={setBody}
              placeholder="Scrivi qui la tua email. Usa la barra per formattare (grassetto, elenchi, link)…"
              testId="admin-broadcast-body"
            />
            <div className="mt-2 flex items-start gap-2 p-2.5 rounded-lg bg-[#d4ff3d]/[0.07] border border-[#d4ff3d]/25">
              <span className="text-[15px] leading-none mt-0.5">👤</span>
              <p className="text-white/70 text-[11px] leading-relaxed">
                Per richiamare automaticamente il nome di ogni contatto, scrivi <code className="px-1 py-0.5 rounded bg-white/10 text-white font-mono">{"{{nome}}"}</code> (o premi <strong className="text-white">“Inserisci nome”</strong>).
                Esempio: <em className="text-white/80">“Ciao {"{{nome}}"}, ho una novità per te”</em> → ogni destinatario riceverà il proprio nome. Se un contatto non ha nome, viene gestito in automatico.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/10 rounded-2xl p-6 space-y-4">
          <div>
            <div className="font-mono-label text-white/50 text-[10px] uppercase tracking-widest mb-2">Anteprima & Prova</div>
            <button
              type="button"
              onClick={openPreview}
              disabled={previewLoading}
              className="w-full bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-white text-sm px-4 py-2.5 rounded-lg transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="admin-broadcast-view-preview"
            >
              {previewLoading ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
              Vedi anteprima email
            </button>
            <div className="mt-3">
              <label className="text-white/50 text-[10px] uppercase tracking-widest font-mono block mb-1.5">Nome di esempio (anteprima/prova)</label>
              <input
                type="text"
                value={sampleName}
                onChange={(e) => setSampleName(e.target.value)}
                placeholder="Es. Frank"
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30"
                data-testid="admin-broadcast-sample-name"
              />
            </div>
            <div className="mt-3">
              <label className="text-white/50 text-[10px] uppercase tracking-widest font-mono block mb-1.5">Invia una prova a</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="tua@email.it"
                  className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30"
                  data-testid="admin-broadcast-test-email"
                />
                <button
                  type="button"
                  onClick={sendTest}
                  disabled={testSending}
                  className="shrink-0 bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-white text-sm px-3 py-2 rounded-lg transition-colors inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="admin-broadcast-test-send"
                >
                  {testSending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Prova
                </button>
              </div>
              {testResult && (
                <div className="mt-2 text-[11px] text-lime" style={{ color: "#d4ff3d" }} data-testid="admin-broadcast-test-result">
                  ✅ Email di prova inviata a {testResult.sent_to}
                </div>
              )}
              <p className="text-white/40 text-[10px] mt-1.5">
                Nell'anteprima e nella prova il tag {"{{nome}}"} è sostituito con “{sampleName || "Frank"}”. All'invio reale ogni contatto riceve il proprio nome.
              </p>
            </div>
          </div>

          <div className="pt-4 border-t border-white/10">
            <div className="font-mono-label text-white/50 text-[10px] uppercase tracking-widest mb-2">Passo 1 — Preview</div>
            <button
              type="button"
              onClick={handlePreview}
              className="w-full bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-white text-sm px-4 py-2.5 rounded-lg transition-colors"
              data-testid="admin-broadcast-preview"
            >
              Calcola destinatari
            </button>
            {preview && (
              <div className="mt-3 p-3 rounded-lg bg-white/[0.03] border border-white/10 text-xs">
                <div className="text-white font-display text-lg">{preview.would_send_to} destinatari</div>
                <div className="text-white/50 mt-1">Esempi:</div>
                <ul className="mt-1 space-y-1">
                  {preview.sample_emails?.map((em, i) => <li key={i} className="text-white/70">• {em}</li>)}
                </ul>
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-white/10">
            <div className="font-mono-label text-white/50 text-[10px] uppercase tracking-widest mb-2">Passo 2 — Invia</div>
            <label className="flex items-center gap-2 mb-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={schedule}
                onChange={(e) => setSchedule(e.target.checked)}
                className="accent-[#d4ff3d] w-4 h-4"
                data-testid="admin-broadcast-schedule-toggle"
              />
              <span className="text-white/80 text-xs">Programma invio a data/ora futura</span>
            </label>
            {schedule && (
              <input
                type="datetime-local"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                className="w-full mb-3 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30"
                data-testid="admin-broadcast-schedule-at"
              />
            )}
            <button
              type="button"
              onClick={handleSend}
              disabled={!preview || sending || preview.would_send_to === 0}
              className="btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              data-testid="admin-broadcast-send"
            >
              {sending ? <><Loader2 size={14} className="animate-spin" /> Invio...</> : <><Send size={14} /> {schedule ? "Programma broadcast" : "Invia broadcast"}</>}
            </button>
          </div>

          {result && (
            <div className="mt-4 p-4 rounded-lg border" style={{ background: "rgba(212,255,61,0.06)", borderColor: "rgba(212,255,61,0.3)" }}>
              <div className="font-display text-white text-lg">{result.scheduled_at ? "🕑 Broadcast programmato" : "✅ Invio completato"}</div>
              <div className="text-white/70 text-xs mt-2">
                {result.scheduled_at
                  ? <>Programmato per <strong>{new Date(result.scheduled_at).toLocaleString("it-IT")}</strong><br /></>
                  : null}
                {result.scheduled_at ? "In coda" : "Inviate"}: <strong>{result.sent}</strong><br />
                Fallite: {result.failed}<br />
                Totale destinatari: {result.total}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Statistiche invii */}
      <div className="mt-6 bg-card border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display text-white text-lg">Statistiche invii</div>
            <div className="text-white/40 text-[11px] mt-0.5">Aperture e click arrivano dai webhook di Resend (possono richiedere qualche minuto)</div>
          </div>
          <button
            type="button"
            onClick={loadStats}
            disabled={statsLoading}
            className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/70 hover:text-white inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="admin-broadcast-stats-refresh"
          >
            {statsLoading ? <Loader2 size={12} className="animate-spin" /> : null} Aggiorna
          </button>
        </div>
        {stats.length === 0 ? (
          <div className="text-white/40 text-sm py-4 text-center">Nessun broadcast inviato ancora.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-broadcast-stats-table">
              <thead>
                <tr className="text-white/40 text-[10px] uppercase tracking-widest border-b border-white/10">
                  <th className="text-left font-mono font-normal py-2 pr-3">Oggetto</th>
                  <th className="text-right font-mono font-normal py-2 px-2">Inviate</th>
                  <th className="text-right font-mono font-normal py-2 px-2">Aperte</th>
                  <th className="text-right font-mono font-normal py-2 px-2">Click</th>
                  <th className="text-right font-mono font-normal py-2 pl-2">Data</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((c) => {
                  const openRate = c.sent ? Math.round((c.opened / c.sent) * 100) : 0;
                  const clickRate = c.sent ? Math.round((c.clicked / c.sent) * 100) : 0;
                  return (
                    <tr key={c.id} className="border-b border-white/5">
                      <td className="py-2.5 pr-3 text-white max-w-[200px] truncate">
                        {c.subject || "—"}
                        {c.scheduled_at && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full align-middle" style={{ background: "rgba(107,139,255,0.15)", color: "#6b8bff" }}>programmato</span>}
                      </td>
                      <td className="py-2.5 px-2 text-right text-white/80">{c.sent}</td>
                      <td className="py-2.5 px-2 text-right text-white/80">{c.opened} <span className="text-white/30 text-[10px]">({openRate}%)</span></td>
                      <td className="py-2.5 px-2 text-right text-white/80">{c.clicked} <span className="text-white/30 text-[10px]">({clickRate}%)</span></td>
                      <td className="py-2.5 pl-2 text-right text-white/40 text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString("it-IT") : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {previewHtml !== null && (
        <div className="fixed inset-0 z-[140] flex items-center justify-center px-4" onClick={() => setPreviewHtml(null)}>
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
          <div className="relative w-full max-w-2xl bg-[#0f0f0f] border border-white/15 rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="admin-broadcast-preview-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div>
                <div className="font-display text-white text-base">Anteprima email</div>
                <div className="text-white/40 text-[11px]">Così la riceve il contatto (esempio nome: {sampleName || "Frank"})</div>
              </div>
              <button onClick={() => setPreviewHtml(null)} className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white" data-testid="admin-broadcast-preview-close">
                <X size={14} />
              </button>
            </div>
            <iframe
              title="Anteprima email broadcast"
              srcDoc={previewHtml}
              sandbox=""
              className="w-full bg-white"
              style={{ height: "70vh", border: "none" }}
              data-testid="admin-broadcast-preview-frame"
            />
          </div>
        </div>
      )}
    </div>
  );
}


function CampaignsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      setItems(await adminCampaigns());
    } catch (e) {
      console.error("Failed to load campaigns:", e?.response?.data || e?.message);
      setError("Errore nel caricamento delle campagne. Riprova.");
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  const pct = (n, d) => (d > 0 ? ((n / d) * 100).toFixed(1) : "0.0");
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
  const typeLabel = (t) => t === "broadcast" ? "Broadcast" : t === "bulk" ? "Bulk CRM" : t;

  return (
    <div data-testid="campaigns-tab">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl text-white">Statistiche Campagne</h1>
          <p className="text-white/50 text-sm mt-2">Aperture e click di ogni invio massivo (broadcast e bulk dal CRM). I dati arrivano dai webhook di Resend.</p>
        </div>
        <button
          data-testid="campaigns-refresh-btn"
          onClick={() => load(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-white/80 text-sm hover:bg-white/5 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Aggiorna
        </button>
      </div>

      {loading ? (
        <div className="text-white/60 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Caricamento...</div>
      ) : error ? (
        <div className="bg-card border border-red-500/30 rounded-2xl p-8 text-center" data-testid="campaigns-error">
          <AlertCircle size={28} className="mx-auto text-red-400 mb-3" />
          <p className="text-white/70 text-sm">{error}</p>
          <button onClick={() => load()} className="mt-4 px-5 py-2 rounded-full bg-white text-black text-sm font-medium hover:bg-white/90 transition-colors">Riprova</button>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-card border border-white/10 rounded-2xl p-8 text-center">
          <BarChart3 size={28} className="mx-auto text-white/40 mb-3" />
          <p className="text-white/60 text-sm">Nessuna campagna inviata ancora. Le statistiche appariranno dopo il primo invio massivo.</p>
        </div>
      ) : (
        <div className="bg-card border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02] border-b border-white/5">
                <tr className="text-left font-mono-label text-[10px] text-white/50 uppercase tracking-widest">
                  <th className="px-4 py-3">Campagna</th>
                  <th className="px-4 py-3">Tipo</th>
                  <th className="px-4 py-3">Inviate</th>
                  <th className="px-4 py-3">Consegnate</th>
                  <th className="px-4 py-3">Aperte</th>
                  <th className="px-4 py-3">Click</th>
                  <th className="px-4 py-3">Data</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`campaign-${c.id}`}>
                    <td className="px-4 py-3 text-white max-w-[280px] truncate">{c.subject || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border border-white/10 text-white/70">{typeLabel(c.type)}</span>
                    </td>
                    <td className="px-4 py-3 font-display text-white">{c.sent}</td>
                    <td className="px-4 py-3 text-white/70">{c.delivered}</td>
                    <td className="px-4 py-3">
                      <span className="font-display text-white">{c.opened}</span>
                      <span className="text-white/40 text-xs ml-1">({pct(c.opened, c.sent)}%)</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-display" style={{ color: "#d4ff3d" }}>{c.clicked}</span>
                      <span className="text-white/40 text-xs ml-1">({pct(c.clicked, c.sent)}%)</span>
                    </td>
                    <td className="px-4 py-3 text-white/50 text-xs">{fmtDate(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-6 text-white/40 text-xs max-w-2xl leading-relaxed">
        💡 Le percentuali di apertura/click si popolano solo dopo aver configurato il webhook di Resend e attivato Open/Click tracking sul dominio. Endpoint webhook: <code className="text-white/60">/api/webhooks/resend</code>
      </div>
    </div>
  );
}


const COURSE_OPTIONS = [
  { id: "controlla-la-tua-comunicazione-digitale", label: "Controlla la Comunicazione Digitale" },
  { id: "social-media-marketing", label: "Social Media Marketing" },
  { id: "facebook-advanced", label: "Meta Ads Advanced" },
  { id: "customer-loyalty", label: "Customer Loyalty & OTO" },
  { id: "agenti-ai", label: "Agenti AI in Azione" },
  { id: "all-in-experience", label: "All-In Experience (tutti i corsi)" },
];

function MaterialsTab() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [productId, setProductId] = useState(COURSE_OPTIONS[0].id);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListMaterials();
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !title.trim()) return;
    setUploading(true); setError(null); setProgress(0);
    try {
      await adminUploadMaterial({
        product_id: productId,
        title: title.trim(),
        file,
        onProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      setTitle(""); setFile(null); setProgress(0);
      document.getElementById("mat-file-input").value = "";
      load();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
    }
  };

  const toggle = async (m) => {
    await adminUpdateMaterial(m.id, { published: !m.published });
    load();
  };
  const remove = async (m) => {
    if (!window.confirm(`Eliminare "${m.title}"?`)) return;
    await adminDeleteMaterial(m.id);
    load();
  };

  const label = (pid) => COURSE_OPTIONS.find((c) => c.id === pid)?.label || pid;
  const fmtSize = (n) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / (1024 * 1024)).toFixed(1)} MB`;

  return (
    <div>
      <h1 className="font-display text-3xl text-white">Materiali corsi</h1>
      <p className="text-white/50 text-sm mt-2 mb-8">
        Upload di PDF, video e workbook. Sono visibili nell'Area Studente solo agli studenti che hanno acquistato il corso selezionato.
      </p>

      {/* Upload form */}
      <form onSubmit={submit} className="bg-card border border-white/10 rounded-2xl p-6 mb-8 max-w-3xl" data-testid="material-upload-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-white/70 text-xs uppercase tracking-widest font-mono mb-2">Corso</label>
            <select
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-white/30"
              data-testid="material-product"
            >
              {COURSE_OPTIONS.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-white/70 text-xs uppercase tracking-widest font-mono mb-2">Titolo</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={200}
              placeholder="Es. Workbook — Modulo 1"
              className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30"
              data-testid="material-title"
            />
          </div>
        </div>

        <label className="block text-white/70 text-xs uppercase tracking-widest font-mono mb-2 mt-5">File (PDF, video, zip — max 100 MB)</label>
        <input
          id="mat-file-input"
          type="file"
          accept=".pdf,.mp4,.mov,.webm,.zip,.doc,.docx,.jpg,.jpeg,.png"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="w-full text-white/80 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-white/10 file:text-white file:cursor-pointer"
          required
          data-testid="material-file"
        />

        {uploading && progress > 0 && (
          <div className="mt-4 h-2 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${progress}%`, background: "#d4ff3d" }} />
          </div>
        )}
        {error && <div className="mt-4 text-red-400 text-sm">{error}</div>}

        <button
          type="submit"
          disabled={uploading || !file || !title.trim()}
          className="btn-primary mt-6 inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="material-submit"
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          Carica materiale
        </button>
      </form>

      {/* List */}
      {loading ? (
        <Loader2 size={20} className="animate-spin text-white/50" />
      ) : items.length === 0 ? (
        <div className="bg-card border border-white/10 rounded-2xl p-8 text-center">
          <FileText size={28} className="mx-auto text-white/40 mb-3" />
          <p className="text-white/60 text-sm">Nessun materiale caricato ancora.</p>
        </div>
      ) : (
        <div className="bg-card border border-white/10 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr className="text-left text-white/50 text-xs uppercase tracking-widest">
                <th className="px-5 py-3 font-normal">Titolo</th>
                <th className="px-5 py-3 font-normal">Corso</th>
                <th className="px-5 py-3 font-normal">File</th>
                <th className="px-5 py-3 font-normal">Stato</th>
                <th className="px-5 py-3 font-normal">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-material-${m.id}`}>
                  <td className="px-5 py-4 text-white">{m.title}</td>
                  <td className="px-5 py-4 text-white/70">{label(m.product_id)}</td>
                  <td className="px-5 py-4 text-white/50 text-xs font-mono">{m.original_filename}<br/>{fmtSize(m.size || 0)}</td>
                  <td className="px-5 py-4">
                    <button
                      type="button"
                      onClick={() => toggle(m)}
                      className={`text-xs rounded-full px-3 py-1 ${m.published ? "text-black" : "text-white/60 border border-white/15"}`}
                      style={m.published ? { background: "#d4ff3d" } : undefined}
                      data-testid={`toggle-${m.id}`}
                    >
                      {m.published ? "Pubblicato" : "Nascosto"}
                    </button>
                  </td>
                  <td className="px-5 py-4">
                    <button
                      type="button"
                      onClick={() => remove(m)}
                      className="text-white/60 hover:text-red-400"
                      data-testid={`delete-${m.id}`}
                      title="Elimina"
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StudentsTab() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      setItems(await adminListStudents());
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
  const filtered = items.filter((u) =>
    !q.trim() || (u.email || "").toLowerCase().includes(q.toLowerCase()) || (u.name || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="students-tab">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-white">Studenti attivi</h1>
          <p className="text-white/50 text-sm mt-2">Account registrati all'Area Studente con corsi acquistati.</p>
        </div>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca per email o nome..."
          className="bg-black/30 border border-white/10 rounded-xl px-4 py-2 text-white text-sm w-64 focus:outline-none focus:border-white/30"
          data-testid="students-search"
        />
      </div>

      {loading ? (
        <Loader2 size={20} className="animate-spin text-white/50" />
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-white/10 rounded-2xl p-8 text-center">
          <GraduationCap size={28} className="mx-auto text-white/40 mb-3" />
          <p className="text-white/60 text-sm">{items.length === 0 ? "Nessuno studente registrato ancora." : "Nessuno studente trovato per la ricerca."}</p>
        </div>
      ) : (
        <div className="bg-card border border-white/10 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr className="text-left text-white/50 text-xs uppercase tracking-widest">
                <th className="px-5 py-3 font-normal">Studente</th>
                <th className="px-5 py-3 font-normal">Corsi</th>
                <th className="px-5 py-3 font-normal">Totale speso</th>
                <th className="px-5 py-3 font-normal">Ultimo login</th>
                <th className="px-5 py-3 font-normal">Registrazione</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.user_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`student-row-${s.user_id}`}>
                  <td className="px-5 py-4">
                    <div className="text-white">{s.name || s.email}</div>
                    <div className="text-white/50 text-xs mt-0.5">{s.email}</div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="text-white">{s.orders_count} ordini</div>
                    {s.completed_courses > 0 && (
                      <div className="text-[10px] mt-0.5" style={{ color: "#d4ff3d" }}>{s.completed_courses} completati</div>
                    )}
                    {s.products.length > 0 && (
                      <div className="text-white/50 text-[11px] mt-1 max-w-xs truncate" title={s.products.join(", ")}>{s.products.join(" · ")}</div>
                    )}
                  </td>
                  <td className="px-5 py-4 font-display text-white" style={{ color: "#d4ff3d" }}>€{s.total_spent.toFixed(0)}</td>
                  <td className="px-5 py-4 text-white/70 text-xs">{fmtDate(s.last_login_at)}</td>
                  <td className="px-5 py-4 text-white/50 text-xs">{fmtDate(s.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}



// ============================================================
// DISCOUNTS TAB — codici sconto personalizzati dal CRM
// ============================================================
function DiscountsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [pct, setPct] = useState(10);
  const [days, setDays] = useState(3);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [lastCreated, setLastCreated] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await adminListDiscountCodes());
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setCreating(true);
    try {
      const res = await adminCreateDiscountCode({ email: email.trim(), pct: Number(pct), days_valid: Number(days) });
      setLastCreated(res);
      setEmail("");
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setCreating(false);
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Eliminare il codice ${code}?`)) return;
    try {
      await adminDeleteDiscountCode(code);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  const copyMsg = (c) => {
    const exp = c.expires_at ? new Date(c.expires_at).toLocaleDateString("it-IT") : "";
    const txt = `Ciao! Ecco il tuo codice sconto personale del ${c.pct}% per il corso MyDigitalAction: ${c.code}\nValido fino al ${exp}. Usalo al checkout su https://mydigitalaction.it`;
    navigator.clipboard.writeText(txt);
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div data-testid="discounts-tab">
      <div className="mb-6">
        <h1 className="font-display text-3xl lg:text-4xl text-white">Codici sconto CRM</h1>
        <p className="text-white/50 text-sm mt-2">Genera un codice personale da inviare a mano (WhatsApp, email) a un contatto indeciso.</p>
      </div>

      <form onSubmit={submit} className="bg-card border border-white/10 rounded-2xl p-6 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input
          type="email"
          required
          placeholder="Email cliente"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="bg-black/30 border border-white/10 rounded-xl px-4 py-2 text-white text-sm focus:outline-none focus:border-white/30"
          data-testid="discount-email-input"
        />
        <input
          type="number"
          min="1"
          max="50"
          value={pct}
          onChange={(e) => setPct(e.target.value)}
          className="bg-black/30 border border-white/10 rounded-xl px-4 py-2 text-white text-sm focus:outline-none focus:border-white/30"
          data-testid="discount-pct-input"
          placeholder="% sconto"
        />
        <input
          type="number"
          min="1"
          max="30"
          value={days}
          onChange={(e) => setDays(e.target.value)}
          className="bg-black/30 border border-white/10 rounded-xl px-4 py-2 text-white text-sm focus:outline-none focus:border-white/30"
          data-testid="discount-days-input"
          placeholder="Giorni validi"
        />
        <button
          type="submit"
          disabled={creating}
          className="rounded-xl px-4 py-2 text-black font-medium disabled:opacity-50"
          style={{ background: "#d4ff3d" }}
          data-testid="discount-create-btn"
        >
          {creating ? "Genero..." : "Genera codice"}
        </button>
        {error && <div className="md:col-span-4 text-red-400 text-sm" data-testid="discount-error">{error}</div>}
        {lastCreated && (
          <div className="md:col-span-4 flex items-center justify-between gap-3 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3" data-testid="discount-last-created">
            <div className="text-sm">
              <span className="text-white/60">Codice pronto:</span>{" "}
              <span className="font-mono text-white font-semibold">{lastCreated.code}</span>{" "}
              <span className="text-white/50">— {lastCreated.pct}% per {lastCreated.email}</span>
            </div>
            <button
              type="button"
              onClick={() => copyMsg(lastCreated)}
              className="text-xs px-3 py-1.5 rounded-lg border border-white/20 text-white hover:bg-white/5 flex items-center gap-1"
              data-testid="discount-copy-msg-btn"
            >
              <Copy size={12} /> Copia messaggio
            </button>
          </div>
        )}
      </form>

      {loading ? (
        <Loader2 size={20} className="animate-spin text-white/50" />
      ) : items.length === 0 ? (
        <div className="bg-card border border-white/10 rounded-2xl p-8 text-center">
          <Ticket size={28} className="mx-auto text-white/40 mb-3" />
          <p className="text-white/60 text-sm">Nessun codice sconto ancora generato.</p>
        </div>
      ) : (
        <div className="bg-card border border-white/10 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr className="text-left text-white/50 text-xs uppercase tracking-widest">
                <th className="px-5 py-3 font-normal">Codice</th>
                <th className="px-5 py-3 font-normal">Email</th>
                <th className="px-5 py-3 font-normal">Sconto</th>
                <th className="px-5 py-3 font-normal">Origine</th>
                <th className="px-5 py-3 font-normal">Stato</th>
                <th className="px-5 py-3 font-normal">Scade</th>
                <th className="px-5 py-3 font-normal"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.code} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`discount-row-${c.code}`}>
                  <td className="px-5 py-4 font-mono text-white">{c.code}</td>
                  <td className="px-5 py-4 text-white/80">{c.email}</td>
                  <td className="px-5 py-4 text-white">-{c.pct}%</td>
                  <td className="px-5 py-4 text-white/50 text-xs">{c.source}</td>
                  <td className="px-5 py-4">
                    <span
                      className="text-xs px-2 py-1 rounded-full"
                      style={{
                        background: c.status === "attivo" ? "rgba(212,255,61,0.15)" : c.status === "usato" ? "rgba(255,255,255,0.05)" : "rgba(239,68,68,0.15)",
                        color: c.status === "attivo" ? "#d4ff3d" : c.status === "usato" ? "rgba(255,255,255,0.5)" : "#f87171",
                      }}
                    >
                      {c.status}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-white/70 text-xs">{fmtDate(c.expires_at)}</td>
                  <td className="px-5 py-4 text-right">
                    {c.status !== "usato" && (
                      <>
                        <button
                          onClick={() => copyMsg(c)}
                          className="text-xs text-white/60 hover:text-white mr-3"
                          title="Copia messaggio pronto"
                          data-testid={`discount-copy-${c.code}`}
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          onClick={() => remove(c.code)}
                          className="text-xs text-red-400 hover:text-red-300"
                          data-testid={`discount-delete-${c.code}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// A/B TEST TAB — statistiche varianti landing
// ============================================================
function ABTestTab() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setStats(await adminAbStats());
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const winner = (row) => {
    const a = row.A.ctr;
    const b = row.B.ctr;
    if (Math.abs(a - b) < 0.5 || (row.A.views < 30 && row.B.views < 30)) return null;
    return a > b ? "A" : "B";
  };

  return (
    <div data-testid="abtest-tab">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl text-white">A/B Test Landing</h1>
          <p className="text-white/50 text-sm mt-2">Titolo e CTA variante A (originale) vs B (alternativa). Ogni visitatore vede la stessa variante su tutte le landing.</p>
        </div>
        <button
          onClick={load}
          className="text-xs px-3 py-2 rounded-lg border border-white/20 text-white hover:bg-white/5 flex items-center gap-1"
          data-testid="abtest-refresh-btn"
        >
          <RefreshCw size={12} /> Aggiorna
        </button>
      </div>

      {loading ? (
        <Loader2 size={20} className="animate-spin text-white/50" />
      ) : error ? (
        <div className="text-red-400 text-sm">{error}</div>
      ) : stats.length === 0 ? (
        <div className="bg-card border border-white/10 rounded-2xl p-8 text-center">
          <FlaskConical size={28} className="mx-auto text-white/40 mb-3" />
          <p className="text-white/60 text-sm">Nessuna visualizzazione landing ancora tracciata.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {stats.map((row) => {
            const w = winner(row);
            return (
              <div key={row.landing_slug} className="bg-card border border-white/10 rounded-2xl p-6" data-testid={`abtest-row-${row.landing_slug}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="font-display text-white text-lg uppercase tracking-widest">/landing-corso-{row.landing_slug}</div>
                  {w && (
                    <span
                      className="text-xs px-3 py-1 rounded-full"
                      style={{ background: "rgba(212,255,61,0.15)", color: "#d4ff3d" }}
                      data-testid={`abtest-winner-${row.landing_slug}`}
                    >
                      Vincente: variante {w}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {["A", "B"].map((v) => (
                    <div
                      key={v}
                      className="rounded-xl p-4 border"
                      style={{
                        borderColor: w === v ? "#d4ff3d" : "rgba(255,255,255,0.1)",
                        background: w === v ? "rgba(212,255,61,0.05)" : "rgba(255,255,255,0.02)",
                      }}
                    >
                      <div className="text-white/50 text-xs uppercase tracking-widest mb-2">Variante {v}</div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <div className="text-white/40 text-[10px]">Views</div>
                          <div className="text-white font-display text-xl">{row[v].views}</div>
                        </div>
                        <div>
                          <div className="text-white/40 text-[10px]">Click CTA</div>
                          <div className="text-white font-display text-xl">{row[v].clicks}</div>
                        </div>
                        <div>
                          <div className="text-white/40 text-[10px]">CTR</div>
                          <div className="font-display text-xl" style={{ color: "#d4ff3d" }}>{row[v].ctr}%</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function BookingsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await adminOnboardingBookings());
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const fmtWhen = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };
  const waLink = (phone) => {
    const digits = (phone || "").replace(/\D/g, "");
    if (!digits) return null;
    let d = digits;
    if ((phone || "").trim().startsWith("00")) d = digits.slice(2);
    else if (!(phone || "").trim().startsWith("+") && digits.length === 10 && digits.startsWith("3")) d = "39" + digits;
    return `https://wa.me/${d}`;
  };

  return (
    <div data-testid="bookings-tab">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl text-white">Prenotazioni call</h1>
          <p className="text-white/50 text-sm mt-2">Le call orientative prenotate su Cal.com dal funnel quiz.</p>
        </div>
        <button
          onClick={load}
          className="text-xs px-3 py-2 rounded-lg border border-white/20 text-white hover:bg-white/5 flex items-center gap-1"
          data-testid="bookings-refresh-btn"
        >
          <RefreshCw size={12} /> Aggiorna
        </button>
      </div>

      {loading ? (
        <Loader2 size={20} className="animate-spin text-white/50" />
      ) : error ? (
        <div className="text-red-400 text-sm" data-testid="bookings-error">{error}</div>
      ) : items.length === 0 ? (
        <div className="bg-card border border-white/10 rounded-2xl p-8 text-center">
          <CalendarClock size={28} className="mx-auto text-white/40 mb-3" />
          <p className="text-white/60 text-sm">Nessuna call prenotata ancora.</p>
        </div>
      ) : (
        <div className="bg-card border border-white/10 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr className="text-left text-white/50 text-xs uppercase tracking-widest">
                <th className="px-5 py-3 font-normal">Quando</th>
                <th className="px-5 py-3 font-normal">Nome</th>
                <th className="px-5 py-3 font-normal">Email</th>
                <th className="px-5 py-3 font-normal">Telefono</th>
                <th className="px-5 py-3 font-normal">Tipo</th>
                <th className="px-5 py-3 font-normal">Stato</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => {
                const wa = waLink(b.phone);
                const st = b.status || "confirmed";
                const stMap = {
                  confirmed: { label: "Confermata", color: "#d4ff3d", bg: "rgba(212,255,61,0.12)" },
                  rescheduled: { label: "Spostata", color: "#6b8bff", bg: "rgba(107,139,255,0.14)" },
                  cancelled: { label: "Annullata", color: "#ff6b6b", bg: "rgba(255,107,107,0.14)" },
                };
                const s = stMap[st] || stMap.confirmed;
                return (
                  <tr key={b.id} className="border-t border-white/5" data-testid={`booking-row-${b.cal_uid}`}>
                    <td className={`px-5 py-3 whitespace-nowrap ${st === "cancelled" ? "text-white/40 line-through" : "text-white"}`}>{fmtWhen(b.start_time)}</td>
                    <td className="px-5 py-3 text-white">{b.name || "—"}</td>
                    <td className="px-5 py-3 text-white/70">{b.email || "—"}</td>
                    <td className="px-5 py-3 text-white/70 whitespace-nowrap">
                      {b.phone ? (
                        wa ? <a href={wa} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: "#25D366" }}>{b.phone}</a> : b.phone
                      ) : "—"}
                    </td>
                    <td className="px-5 py-3 text-white/50">{b.event_type || "orientamento"}</td>
                    <td className="px-5 py-3">
                      <span className="text-xs px-2.5 py-1 rounded-full whitespace-nowrap" style={{ color: s.color, background: s.bg }} data-testid={`booking-status-${b.cal_uid}`}>{s.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
